import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, CheckCircle2, ShieldCheck } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TOPICS = ["General question", "Help with my account", "Problem with a listing or user", "Privacy request", "Suspension appeal", "Advertising & partnerships", "Other"];

export default function Contact() {
  const { user } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", email: user?.email || "", topic: TOPICS[0], subject: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { if (user) setF((p) => ({ ...p, name: p.name || user.name, email: p.email || user.email })); }, [user]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/contact", { name: f.name, email: f.email, subject: `${f.topic}: ${f.subject}`, message: f.message, website: f.website || null });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (e2) { setErr(formatErr(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">Get in touch</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Contact us</h1>
        <p className="mt-4 leading-relaxed text-slate-300">Have a question, need help with your account, or want to report a problem? Send us a message and the ISoldIt team will reply by email, usually within 1–2 business days.</p>
        <div className="mt-8 space-y-4 text-sm">
          <p className="flex items-start gap-3 rounded-2xl bg-[#173742] p-4"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-[#d4f253]" /><span>Prefer email? Write to <a href="mailto:Admin@isoldit.com.au" data-testid="contact-email-link" className="font-semibold text-[#d4f253]">Admin@isoldit.com.au</a></span></p>
          <p className="flex items-start gap-3 rounded-2xl bg-[#173742] p-4"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#d4f253]" /><span>We will never ask for your password or bank details. To report a listing or user quickly, use the Report button on their page.</span></p>
        </div>
      </div>

      <div className="rise rounded-3xl bg-[#173742] p-6 sm:p-8">
        {sent ? (
          <div data-testid="contact-success" className="py-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-[#d4f253]" />
            <h2 className="mt-4 font-display text-2xl font-bold">Message sent</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-slate-300">Thanks, {f.name.split(" ")[0]}. We've received your message and will reply to <span className="font-semibold text-white">{f.email}</span>.</p>
            <Link to="/" data-testid="contact-home-link" className="btn-lime mt-6 inline-flex px-6 py-3 text-sm">Back to ISoldIt</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div><label className="mb-1.5 block text-xs font-semibold text-slate-300">Your name</label><input data-testid="contact-name-input" required minLength={2} maxLength={80} className="field" value={f.name} onChange={up("name")} /></div>
              <div><label className="mb-1.5 block text-xs font-semibold text-slate-300">Your email (for our reply)</label><input data-testid="contact-email-input" type="email" required className="field" value={f.email} onChange={up("email")} /></div>
            </div>
            <div className="grid gap-4 sm:grid-cols-[0.9fr_1.1fr]">
              <div><label className="mb-1.5 block text-xs font-semibold text-slate-300">Topic</label>
                <select data-testid="contact-topic-select" className="field" value={f.topic} onChange={up("topic")}>{TOPICS.map((t) => <option key={t}>{t}</option>)}</select>
              </div>
              <div><label className="mb-1.5 block text-xs font-semibold text-slate-300">Subject</label><input data-testid="contact-subject-input" required minLength={3} maxLength={90} className="field" value={f.subject} onChange={up("subject")} placeholder="What's it about?" /></div>
            </div>
            <div><label className="mb-1.5 block text-xs font-semibold text-slate-300">Message</label>
              <textarea data-testid="contact-message-input" required minLength={10} maxLength={4000} rows={7} className="field h-auto py-3" value={f.message} onChange={up("message")} placeholder="Tell us how we can help. Include a link to the listing if it's about an item." />
              <p className="mt-1 text-right text-[11px] text-slate-500">{f.message.length}/4000</p>
            </div>
            <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={f.website} onChange={up("website")} name="website" />
            {err && <p data-testid="contact-error" className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">{err}</p>}
            <button data-testid="contact-submit-btn" disabled={busy} className="btn-lime w-full py-3.5 text-sm">{busy ? "Sending…" : "Send message"}</button>
            <p className="text-center text-xs text-slate-500">By sending, you agree we can use your details to reply, as described in our <Link to="/privacy" className="text-[#d4f253] hover:underline">Privacy Policy</Link>.</p>
          </form>
        )}
      </div>
    </div>
  );
}
